<template>
  <div class="column-edit-container pr-3">
    <t-checkbox-group
      :checked="selectedColumnSetKeys"
      name="defaultColumnSets"
      value-field="key"
      text-field="name"
      stacked
      @updated="updateSelectedColumnSets"
    >
      <t-checkbox
        v-for="defaultSet in defaultColumnSets"
        :key="defaultSet.key"
        :value="defaultSet.key"
        class="py-1"
        >{{ defaultSet.name }}</t-checkbox
      >
    </t-checkbox-group>
    <hr
      v-if="defaultColumnSets.length > 0 && customColumnSets.length > 0"
      class="border-top"
    />
    <t-checkbox-group
      :checked="selectedColumnSetKeys"
      name="customColumnSets"
      value-field="key"
      text-field="name"
      stacked
      @updated="updateSelectedColumnSets"
    >
      <template v-for="customSet in customColumnSets">
        <column-edit-actions
          :custom-set="customSet"
          @saveColumn="saveCustomColumnSets"
          @shareColumn="shareColumnSet($event)"
          @removeColumn="removeCustomColumnSet($event)"
          @saveColumnsIntoSet="saveFieldsIntoCustomColumnSet($event)"
        />
      </template>
    </t-checkbox-group>

    <hr
      v-if="defaultColumnSets.concat(customColumnSets).length > 0"
      class="border-top"
    />
    <t-form-group>
      <template #label>
        <div class="d-flex justify-content-between align-items-end">
          <t-checkbox
            :checked="selectedColumnSetKeys.length === 0 && !isFieldListDefault"
            disabled
            name="columnSets"
            class="d-inline-block"
            >Custom</t-checkbox
          >
          <t-button
            variant="link"
            class="p-0"
            @click="saveAsOpen = !saveAsOpen"
            >Save As</t-button
          >
        </div>
      </template>

      <simple-cancel-save
        :model-value="''"
        :show="saveAsOpen"
        @update:show="saveAsOpen = !saveAsOpen"
        @save="addCustomColumnSet({ name: $event })"
      >
        <template #edit="{ value, update }">
          <t-form-group label="Give it a name">
            <p-input-text
              :model-value="value"
              placeholder="My Columns"
              @update:modelValue="update($event)"
            />
          </t-form-group>
        </template>
      </simple-cancel-save>

      <t-checkbox-group
        :checked="visibleFields"
        name="fieldGroups"
        stacked
        class="ml-3"
      >
        <template
          v-for="(field, fieldIndex) in fieldList"
          :key="`getFieldKey(field)-${fieldIndex}`"
        >
          <div
            v-if="!excludedColumns.includes(getFieldKey(field))"
            :ref="`draggable${fieldIndex}`"
            class="draggable hover-highlight py-1 px-2"
            @dragover.prevent="dragOver($event, fieldIndex)"
            @dragenter="dragEnter($event, fieldIndex)"
            @dragleave="dragLeave($event, fieldIndex)"
            @drop.prevent="drop($event, fieldIndex)"
          >
            <div
              :draggable="true"
              class="drag-inside m-0 py-1 d-flex justify-content-between align-items-center"
              @dragstart="dragStart($event, fieldIndex)"
              @dragend="dragEnd($event, fieldIndex)"
            >
              <t-checkbox
                :value="field"
                class=""
                @change="checkboxClick(field, fieldIndex)"
                >{{ renderFieldText(field) }}</t-checkbox
              >
              <fluency-icon
                class="fluency-gray"
                type="move"
              />
            </div>
          </div>
        </template>
      </t-checkbox-group>
    </t-form-group>
    <t-button
      variant="link"
      class="p-0 mb-2 ml-2"
      @click.stop="revert"
      >Revert to Default</t-button
    >
  </div>
</template>

<script>
import columnSets, { columnSetSkeleton } from 'core-ui/assets/js/constants/columnSets'
import md5 from 'md5'
import SimpleCancelSave from 'core-ui/components/common/simpleCancelSave'
import _cloneDeep from 'lodash-es/cloneDeep'
import ColumnEditActions from 'core-ui/components/common/columnEditActions'

export default {
  name: 'columnEdit',
  components: { ColumnEditActions, SimpleCancelSave },
  props: {
    defaultFields: {
      type: Array // array of the default fields for the columnKey
    },
    columnKey: {
      type: String
    }
  },
  data () {
    return {
      dragging: false,
      draggingOver: [],
      excludedColumns: ['selectRow', 'budgetName', 'name', 'keywordText', 'creativePlan', 'extensionName', 'budgetShared'],
      visibleFields: [], // keeping a local copy of the visible fields.
      fieldList: [],
      selectedColumnSetKeys: [],
      customColumnSets: [],
      dragOverFieldIndex: -1,
      saveAsOpen: false,
      editNameOpen: false,
      shareColumnOpen: false
    }
  },
  computed: {
    defaultFieldMap () {
      return this.defaultFields.toMap(this.getFieldKey)
    },
    defaultColumnSets () {
      return columnSets.getConfigsForTable(this.columnKey, this.$store.getters.user.capabilities)
    },
    isFieldListDefault () {
      if (this.visibleFields.length !== this.defaultFields.length) {
        return false
      }
      // compare the field keys of fieldlist and default fields. return true if they're all equal in the same order
      return Array.zip(this.fieldList, this.defaultFields, (field, defaultField) => this.getFieldKey(field) === this.getFieldKey(defaultField)).every(
        (val) => val
      )
    }
  },
  watch: {
    columnKey: {
      immediate: true,
      handler: async function (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          await Promise.all([
            this.$store.dispatch('table/getColumns', { columnKey: newVal, type: 'order' }),
            this.$store.dispatch('table/getColumns', { columnKey: newVal, type: 'sets' })
          ])

          this.$setCompat(this, 'visibleFields', this.$store.getters['table/visibleFields'](newVal, [...this.defaultFields]))
          this.$setCompat(this, 'customColumnSets', _cloneDeep(this.$store.getters['table/columns'][newVal]?.sets ?? []))
          this.selectedColumnSetKeys.splice(0, this.selectedColumnSetKeys.length, ...this.$store.getters['table/selectedColumnSets'](newVal))
          this.applyColumnSets(false)
        }
      }
    },
    defaultFields: {
      immediate: true,
      handler: function (newVal) {
        this.$setCompat(this, 'fieldList', [...(newVal || [])])
      }
    }
  },
  methods: {
    async saveColumnOrder (isRevert) {
      const columnList = this.fieldList
        .filter((field) => !!field)
        .map(this.getFieldKey)
        .map((fieldKey, indx) => ({
          columnName: fieldKey, // JSON.stringify(fieldKey),
          columnOrder: indx,
          visible: isRevert || this.visibleFields.findIndex((field) => this.fieldHasKey(field, fieldKey)) !== -1
        }))
      const columnObject = {
        columnList,
        selectedColumnSets: [...this.selectedColumnSetKeys]
      }
      await this.$store.dispatch('table/setColumns', {
        columnKey: this.columnKey,
        columnObject,
        type: 'order'
      })
      this.$emit('update', columnObject)
    },
    checkboxClick (field, fieldIndex) {
      const visibleIndex = this.visibleFields.indexOf(field)
      if (visibleIndex === -1) {
        this.visibleFields.splice(Math.min(fieldIndex, this.visibleFields.length), 0, field) // insert
      } else {
        this.visibleFields.splice(visibleIndex, 1) // remove
      }
      this.selectedColumnSetKeys.splice(0, this.selectedColumnSetKeys.length)
      this.$nextTick(() => this.saveColumnOrder())
    },
    async revert () {
      const cfm = await this.$confirm('Are you sure you want to revert these fields to default?')
      if (cfm) {
        this.$setCompat(this, 'fieldList', [...this.defaultFields])
        this.$setCompat(this, 'visibleFields', [...this.defaultFields])
        this.selectedColumnSetKeys.splice(0, this.selectedColumnSetKeys.length)
        this.saveColumnOrder(true)
      }
    },
    renderFieldText (field) {
      const fieldKey = this.getFieldKey(field)
      if (field.label) {
        return field.label
      } else if (this.defaultFieldMap[fieldKey] && this.defaultFieldMap[fieldKey].label) {
        return this.defaultFieldMap[fieldKey].label
      } else {
        return this.$filters.convertFromCamelCase(fieldKey)
      }
    },

    dragStart: function (e, n) {
      e.dataTransfer.setData('text', n)

      this.dragging = true
      this.addClass(n, 'dragging')
    },
    dragEnd: function (e, n) {
      this.dragging = false
      this.removeClass(n, 'dragging')
    },
    dragEnter: function (e, fieldIndex) {
      this.addClass(fieldIndex, 'dragging-over')
    },
    dragLeave: function (e, fieldIndex) {
      this.removeClass(fieldIndex, 'dragging-over')
    },
    dragOver: function (e, fieldIndex) {
      if (this.dragOverFieldIndex !== fieldIndex) {
        this.addClass(fieldIndex, 'dragging-over')
      }
      this.dragOverFieldIndex = fieldIndex
    },
    drop: function (e, fieldIndex) {
      const droppedIndex = parseInt(e.dataTransfer.getData('text'))
      this.dragging = false
      this.$refs[`draggable${fieldIndex}`][0].classList.remove('dragging')
      this.removeClass(fieldIndex, 'dragging-over')
      this.draggingOver[fieldIndex] = false
      this.fieldList.move(droppedIndex, fieldIndex) // from extensions.js
      this.selectedColumnSetKeys.splice(0, this.selectedColumnSetKeys.length)
      this.saveColumnOrder()
      this.dragOverFieldIndex = -1
    },
    prevent: function (e) {
      e.preventDefault()
    },
    addClass (fieldIndex, className) {
      if (this.$refs[`draggable${fieldIndex}`]?.[0]?.classList) {
        this.$refs[`draggable${fieldIndex}`][0].classList.add(className)
      }
    },
    removeClass (fieldIndex, className) {
      if (this.$refs[`draggable${fieldIndex}`]?.[0]?.classList) {
        this.$refs[`draggable${fieldIndex}`][0].classList.remove(className)
      }
    },
    updateSelectedColumnSets (value) {
      this.selectedColumnSetKeys = value
      this.applyColumnSets()
    },
    applyColumnSets (save = true) {
      this.visibleFields.splice(0, this.visibleFields.length)
      const originalFields = this.fieldList.splice(0, this.fieldList.length)
      const addedFields = new Set()
      const keys = this.selectedColumnSetKeys.flatMap(
        (key) => this.defaultColumnSets.find((s) => s.key === key)?.fields ?? this.customColumnSets.find((s) => s.key === key)?.fields ?? []
      ) // concat the field lists together
      if (keys.length === 0) {
        keys.push(...this.$store.getters['table/visibleFields'](this.columnKey, this.defaultFields).map(this.getFieldKey))
      }
      keys.forEach((fieldKey, index) => {
        if (!addedFields.has(fieldKey)) {
          // don't add it twice
          addedFields.add(fieldKey)
          const field = this.defaultFieldMap[fieldKey]
          if (field) {
            this.visibleFields.push(field)
            this.fieldList.push(field)

            const originalFieldIndex = originalFields.findIndex((originalField) => this.fieldHasKey(originalField, fieldKey))

            if (originalFieldIndex !== -1) {
              originalFields.splice(originalFieldIndex, 1)
            }
          }
        }
      })
      this.fieldList.push(...originalFields)
      if (save) {
        this.saveColumnOrder()
      }
    },
    addCustomColumnSet ({ name }) {
      if (!name || name.length === 0) {
        return
      }
      const customFieldKeys = this.getVisibleFieldKeys()

      const key = md5(`${name}:${customFieldKeys.join(',')}`)

      this.customColumnSets.push({
        ...columnSetSkeleton,
        key: key,
        name: name || 'My Column Set',
        tables: [this.columnKey],
        fields: customFieldKeys
      })

      this.selectedColumnSetKeys.splice(0, this.selectedColumnSetKeys.length, key)

      this.saveCustomColumnSets()
    },
    saveCustomColumnSets () {
      this.$store.dispatch('table/setColumns', {
        columnKey: this.columnKey,
        columnObject: _cloneDeep(this.customColumnSets),
        type: 'sets'
      })
      this.saveColumnOrder()
    },
    saveFieldsIntoCustomColumnSet (columnSet) {
      const fieldKeys = this.getVisibleFieldKeys()

      columnSet.fields.splice(0, columnSet.fields.length, ...fieldKeys)
      this.selectedColumnSetKeys.splice(0, this.selectedColumnSetKeys.length, columnSet.key)
      this.saveCustomColumnSets()
    },
    async removeCustomColumnSet (columnSet) {
      this.customColumnSets.remove(columnSet)
      this.selectedColumnSetKeys.remove(columnSet.key)

      this.saveCustomColumnSets()
    },
    async shareColumnSet ({ columnSet, shareWith }) {
      const response = await this.$res.set.shareColumnSet({ forTable: `${this.columnKey}_sets`, columnSet, shareWith })
    },
    getVisibleFieldKeys () {
      return this.fieldList
        .filter((field) => !!field)
        .map(this.getFieldKey)
        .filter((fieldKey) => this.visibleFields.findIndex((visField) => this.fieldHasKey(visField, fieldKey)) !== -1)
    },
    getFieldKey (field) {
      return field?.slot || field?.key || field
    },
    fieldHasKey (field, key) {
      return this.getFieldKey(field) === key
    }
  }
}
</script>

<style lang="scss" scoped>
.draggable {
  cursor: grab;
  border-top: 2px solid white;

  * {
    cursor: grab;
  }

  .iconic {
    visibility: hidden;
  }

  &:hover .iconic {
    visibility: visible;
  }

  &:active {
    cursor: grabbing;
  }
}

.dragging-over {
  border-top: 2px solid #ccc;
}

.column-edit-container {
  max-height: 70vh;
  width: 15rem;
  overflow-y: auto;
}

.customSet .hover-item {
  visibility: hidden;
}
.customSet:hover .hover-item {
  visibility: visible;
}
</style>
